import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useToast } from "../../hooks/useToast";
import { apiClient } from "../../services/api";

const useStyles = FC.makeStyles({
    root: { display: "flex", flexDirection: "column", ...FC.shorthands.gap("12px"), ...FC.shorthands.padding("16px") },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between" },
    grid: { display: "grid", gridTemplateColumns: "minmax(360px, 1fr) minmax(420px, 1.3fr)", ...FC.shorthands.gap("12px") },
    panel: { ...FC.shorthands.border("1px", "solid", "#ddd"), ...FC.shorthands.borderRadius("6px"), ...FC.shorthands.padding("14px") },
    form: { display: "flex", flexDirection: "column", ...FC.shorthands.gap("10px") },
    row: { display: "grid", gridTemplateColumns: "1fr 1fr", ...FC.shorthands.gap("10px") },
    item: { display: "flex", justifyContent: "space-between", alignItems: "center", ...FC.shorthands.padding("10px"), cursor: "pointer" },
    selected: { backgroundColor: "#e8f3ff" },
    rentals: { display: "flex", flexDirection: "column", ...FC.shorthands.gap("8px") },
    rental: { display: "grid", gridTemplateColumns: "1fr auto", ...FC.shorthands.gap("8px"), ...FC.shorthands.padding("8px"), backgroundColor: "#f6f6f6" },
    mono: { fontFamily: "monospace", fontSize: "12px" },
});

const emptyDevice = {
    seq: "",
    name: "",
    nucDeviceId: "",
    deviceKeyId: "",
    publicKeyPem: "",
    status: "registered",
    notes: "",
    version: 1,
};

const toUtc = (value) => value ? new Date(value).toISOString() : null;

export const DeviceManagement = () => {
    const styles = useStyles();
    const showToast = useToast();
    const [devices, setDevices] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [draft, setDraft] = useState(emptyDevice);
    const [busy, setBusy] = useState(false);
    const [rental, setRental] = useState({ tenantId: "", startsAtUtc: "", endsAtUtc: "", notes: "" });

    const selected = useMemo(
        () => devices.find((item) => item.id === selectedId) || null,
        [devices, selectedId]
    );

    const refresh = useCallback(async () => {
        const [deviceRows, tenantRows] = await Promise.all([
            apiClient.getDevices(),
            apiClient.getTenants({ status: "active" }),
        ]);
        setDevices(deviceRows || []);
        setTenants((tenantRows || []).filter((item) => item.status === "active"));
    }, []);

    useEffect(() => {
        refresh().catch((error) => showToast("加载设备失败", "error", error.message));
    }, [refresh, showToast]);

    useEffect(() => {
        setDraft(selected ? { ...selected } : { ...emptyDevice });
    }, [selected]);

    const set = (name) => (event) => setDraft((value) => ({ ...value, [name]: event.target.value }));

    const save = async () => {
        setBusy(true);
        try {
            if (selected) {
                await apiClient.updateDevice(selected.id, {
                    version: selected.version,
                    name: draft.name,
                    deviceKeyId: draft.deviceKeyId || null,
                    publicKeyPem: draft.publicKeyPem,
                    status: draft.status,
                    notes: draft.notes || null,
                });
            } else {
                const created = await apiClient.createDevice({
                    seq: draft.seq,
                    name: draft.name,
                    nucDeviceId: draft.nucDeviceId,
                    deviceKeyId: draft.deviceKeyId || null,
                    publicKeyPem: draft.publicKeyPem,
                    notes: draft.notes || null,
                });
                setSelectedId(created.id);
            }
            await refresh();
            showToast("设备已保存", "success");
        } catch (error) {
            showToast("保存设备失败", "error", `${error.errorCode || ""} ${error.message}`.trim());
        } finally {
            setBusy(false);
        }
    };

    const addRental = async () => {
        if (!selected) return;
        setBusy(true);
        try {
            await apiClient.createDeviceRental(selected.id, {
                tenantId: Number(rental.tenantId),
                startsAtUtc: toUtc(rental.startsAtUtc),
                endsAtUtc: toUtc(rental.endsAtUtc),
                notes: rental.notes || null,
            });
            setRental({ tenantId: "", startsAtUtc: "", endsAtUtc: "", notes: "" });
            await refresh();
            showToast("租期已添加", "success");
        } catch (error) {
            showToast("添加租期失败", "error", `${error.errorCode || ""} ${error.message}`.trim());
        } finally {
            setBusy(false);
        }
    };

    const removeRental = async (rentalId) => {
        if (!selected || !window.confirm("确认删除这段租期？该操作会写入审计日志。")) return;
        try {
            await apiClient.deleteDeviceRental(selected.id, rentalId);
            await refresh();
            showToast("租期已删除", "success");
        } catch (error) {
            showToast("删除租期失败", "error", error.message);
        }
    };

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div>
                    <FC.Title2>设备与租期</FC.Title2>
                    <FC.Body1>设备仅保存身份、公钥、状态与租期；租期相邻边界允许，重叠会被拒绝。</FC.Body1>
                </div>
                <FC.Button icon={<IC.AddRegular />} onClick={() => setSelectedId(null)}>登记设备</FC.Button>
            </div>

            <div className={styles.grid}>
                <div className={styles.panel}>
                    {devices.map((item) => (
                        <div
                            key={item.id}
                            className={`${styles.item} ${item.id === selectedId ? styles.selected : ""}`}
                            onClick={() => setSelectedId(item.id)}
                        >
                            <div>
                                <FC.Body1Strong>{item.seq} · {item.name}</FC.Body1Strong>
                                <div className={styles.mono}>{item.nucDeviceId}</div>
                            </div>
                            <FC.Badge appearance="filled" color={item.status === "registered" ? "success" : "danger"}>
                                {item.status === "registered" ? "已登记" : "已禁用"}
                            </FC.Badge>
                        </div>
                    ))}
                    {devices.length === 0 && <FC.Body1>尚无设备。</FC.Body1>}
                </div>

                <div className={styles.panel}>
                    <div className={styles.form}>
                        <FC.Title3>{selected ? `编辑 ${selected.seq}` : "登记新设备"}</FC.Title3>
                        <div className={styles.row}>
                            <FC.Field label="设备编号"><FC.Input value={draft.seq || ""} disabled={!!selected} onChange={set("seq")} /></FC.Field>
                            <FC.Field label="名称"><FC.Input value={draft.name || ""} onChange={set("name")} /></FC.Field>
                        </div>
                        <FC.Field label="NUC Device ID"><FC.Input value={draft.nucDeviceId || ""} disabled={!!selected} onChange={set("nucDeviceId")} /></FC.Field>
                        <FC.Field label="Device Key ID（可选）"><FC.Input value={draft.deviceKeyId || ""} onChange={set("deviceKeyId")} /></FC.Field>
                        <FC.Field label="P-256 公钥（SPKI PEM）">
                            <FC.Textarea rows={7} value={draft.publicKeyPem || ""} onChange={set("publicKeyPem")} className={styles.mono} />
                        </FC.Field>
                        {selected && <FC.Body2 className={styles.mono}>指纹：{selected.publicKeyFingerprint}</FC.Body2>}
                        {selected && (
                            <FC.Field label="状态">
                                <select value={draft.status} onChange={set("status")}>
                                    <option value="registered">registered</option>
                                    <option value="disabled">disabled</option>
                                </select>
                            </FC.Field>
                        )}
                        <FC.Field label="备注"><FC.Textarea value={draft.notes || ""} onChange={set("notes")} /></FC.Field>
                        <FC.Button appearance="primary" disabled={busy} onClick={save}>保存</FC.Button>

                        {selected && (
                            <>
                                <FC.Divider>租期</FC.Divider>
                                <div className={styles.rentals}>
                                    {(selected.rentals || []).map((item) => (
                                        <div key={item.id} className={styles.rental}>
                                            <div>
                                                <FC.Body1Strong>{item.tenantName}</FC.Body1Strong>
                                                <div>{new Date(item.startsAtUtc).toLocaleString()} → {new Date(item.endsAtUtc).toLocaleString()}</div>
                                                {item.notes && <FC.Caption1>{item.notes}</FC.Caption1>}
                                            </div>
                                            <FC.Button icon={<IC.DeleteRegular />} appearance="subtle" onClick={() => removeRental(item.id)} />
                                        </div>
                                    ))}
                                </div>
                                <div className={styles.row}>
                                    <FC.Field label="租户">
                                        <select value={rental.tenantId} onChange={(event) => setRental((value) => ({ ...value, tenantId: event.target.value }))}>
                                            <option value="">请选择</option>
                                            {tenants.map((item) => <option key={item.id} value={item.id}>{item.seq} · {item.name}</option>)}
                                        </select>
                                    </FC.Field>
                                    <FC.Field label="备注"><FC.Input value={rental.notes} onChange={(event) => setRental((value) => ({ ...value, notes: event.target.value }))} /></FC.Field>
                                </div>
                                <div className={styles.row}>
                                    <FC.Field label="开始（本地时间）"><input type="datetime-local" value={rental.startsAtUtc} onChange={(event) => setRental((value) => ({ ...value, startsAtUtc: event.target.value }))} /></FC.Field>
                                    <FC.Field label="结束（本地时间）"><input type="datetime-local" value={rental.endsAtUtc} onChange={(event) => setRental((value) => ({ ...value, endsAtUtc: event.target.value }))} /></FC.Field>
                                </div>
                                <FC.Button disabled={busy || !rental.tenantId || !rental.startsAtUtc || !rental.endsAtUtc} onClick={addRental}>添加租期</FC.Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
